import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { BaseToolPersistedState, ToolConfigParams, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { Capsule } from '@/utilities/datetime.constants';

interface ClusteringState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // monkey() computed value
  modelPropertiesExpanded: boolean;
  color: string;
  numClusters: number;
  inputSignals?: ToolItem[];
  condition?: ToolItem;
  restrictToCondition?: ToolItem;
}

export type ClusteringDehydratedState = Omit<ClusteringState, 'window'>;

export class ClusteringStore extends BaseToolStore {
  static readonly storeName = 'sqClusteringStore';
  type = TREND_TOOLS.CLUSTERING;

  parameterDefinitions = {
    inputSignals: {
      predicate: (parameter: ToolParameter) => !_.includes(['condition', 'restrictToCondition'], parameter.name ?? ''),
      multiple: true,
    },
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
  };

  initialize(): void {
    const initialState: ClusteringState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.getState().displayRange.start,
      windowEnd: sqDurationStore.getState().displayRange.end,
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numClusters: 3,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] | undefined {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get color(): string {
    return this.state.get('color');
  }

  get numClusters(): number {
    return this.state.get('numClusters');
  }

  dehydrate(): ClusteringDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<ClusteringDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setNumClusters(payload: { numClusters: number }): void {
    this.state.set('numClusters', payload.numClusters);
  }

  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    CLUSTERING_SET_TRAINING_WINDOW: this.setTrainingWindow,
    CLUSTERING_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    CLUSTERING_SET_COLOR: this.setColor,
    CLUSTERING_SET_NUM_CLUSTERS: this.setNumClusters,
  };
}
